import React, { useState } from 'react';
import { Users, TrendingUp, Heart, Award, Briefcase, Mail, Upload, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert('Thank you for your application! We will review your resume and get back to you soon.');
  };

  const openings = [
    {
      id: 'mech-design-eng',
      title: 'Mechanical Design Engineer',
      department: 'Engineering',
      location: 'Jaipur, Rajasthan',
      type: 'Full-time',
      experience: '2-5 years',
      description: 'Design and develop mineral water bottling machines using CAD software. Experience in SS fabrication preferred.'
    },
    {
      id: 'plc-programmer',
      title: 'PLC Programmer',
      department: 'Automation',
      location: 'Jaipur, Rajasthan',
      type: 'Full-time',
      experience: '3-6 years',
      description: 'Program and commission PLC systems for bottling machines. Siemens/Allen Bradley experience required.'
    },
    {
      id: 'field-service-eng',
      title: 'Field Service Engineer',
      department: 'Service',
      location: 'Multiple Locations',
      type: 'Full-time',
      experience: '1-3 years',
      description: 'Install, commission, and service mineral water plants across India. Willingness to travel required.'
    },
    {
      id: 'sales-executive',
      title: 'Sales Executive',
      department: 'Sales & Marketing',
      location: 'Jaipur, Rajasthan',
      type: 'Full-time',
      experience: '2-4 years',
      description: 'Business development and client relationship management in beverage industry. Technical background preferred.'
    }
  ];

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title="Careers at Alden Technologies | Join Our Engineering Team"
        description="Explore career opportunities at Alden Technologies — engineering, service, sales and manufacturing roles in the beverage machinery industry."
        path="/careers"
      />
      <Breadcrumbs items={[{ label: 'Careers' }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green-dark via-alden-green to-alden-green-light text-white py-20 md:py-28">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 px-6 py-3 rounded-full text-sm font-semibold mb-6">
            💼 Join Our Team
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Careers at <span className="text-yellow-300">Alden Technologies</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 leading-relaxed">
            Be part of India&apos;s leading mineral water plant manufacturer. Build innovative solutions and grow your career with us.
          </p>
        </div>
      </section>

      {/* Why Join Alden */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Join <span className="text-alden-green">Alden Technologies?</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Work with cutting-edge technology, learn from industry experts, and build a rewarding career.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: TrendingUp, title: 'Career Growth', desc: 'Fast-track career progression with skill development programs' },
              { icon: Award, title: 'Industry Leader', desc: 'Work with 100+ successful projects and learn from the best' },
              { icon: Heart, title: 'Work-Life Balance', desc: 'Flexible work environment and employee wellness programs' },
              { icon: Users, title: 'Great Team', desc: 'Collaborative culture with experienced mentors and supportive colleagues' }
            ].map((benefit, idx) => (
              <Card key={idx} className="border-2 border-alden-green/20 hover:border-alden-green transition-all text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-alden-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-10 h-10 text-alden-green" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Employee Benefits */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Employee <span className="text-alden-green">Benefits</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive benefits package designed for your well-being and growth.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { id: 'benefit-salary', text: 'Competitive Salary Package' },
              { id: 'benefit-health', text: 'Health Insurance' },
              { id: 'benefit-bonus', text: 'Performance Bonuses' },
              { id: 'benefit-skill', text: 'Skill Development Programs' },
              { id: 'benefit-cert', text: 'Professional Certifications' },
              { id: 'benefit-travel', text: 'Travel Allowances' },
              { id: 'benefit-festival', text: 'Festival Bonuses' },
              { id: 'benefit-leave', text: 'Paid Leaves' },
              { id: 'benefit-retire', text: 'Retirement Benefits' }
            ].map((benefit) => (
              <div key={benefit.id} className="flex items-center gap-3 bg-white p-4 rounded-lg border-2 border-alden-green/20">
                <CheckCircle className="w-6 h-6 text-alden-green flex-shrink-0" />
                <span className="font-semibold text-gray-900">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Current <span className="text-alden-green">Openings</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore exciting career opportunities across engineering, service, and sales departments.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {openings.map((job) => (
              <Card key={job.id} className="border-2 border-alden-green/20 hover:border-alden-green hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h3>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className="bg-alden-green/10 text-alden-green px-3 py-1 rounded-full font-semibold">
                          {job.department}
                        </span>
                        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                          {job.location}
                        </span>
                        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                          {job.type}
                        </span>
                        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                          {job.experience}
                        </span>
                      </div>
                    </div>
                    <Button className="bg-alden-green hover:bg-alden-green-light text-white whitespace-nowrap">
                      Apply Now
                    </Button>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{job.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upload Resume Form */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Apply Now
              </h2>
              <p className="text-lg text-gray-600">
                Submit your resume and our HR team will contact you for suitable openings.
              </p>
            </div>

            <Card className="border-2 border-alden-green/20">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-alden-green focus:outline-none"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-alden-green focus:outline-none"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                      <input
                        type="tel"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-alden-green focus:outline-none"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Position Applied For *</label>
                      <select
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-alden-green focus:outline-none"
                        value={formData.position}
                        onChange={(e) => setFormData({...formData, position: e.target.value})}
                      >
                        <option value="">Select Position</option>
                        {openings.map((job) => (
                          <option key={job.id} value={job.title}>{job.title}</option>
                        ))}
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Total Experience *</label>
                      <select
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-alden-green focus:outline-none"
                        value={formData.experience}
                        onChange={(e) => setFormData({...formData, experience: e.target.value})}
                      >
                        <option value="">Select Experience</option>
                        <option value="0-1">0-1 years</option>
                        <option value="1-3">1-3 years</option>
                        <option value="3-5">3-5 years</option>
                        <option value="5-10">5-10 years</option>
                        <option value="10+">10+ years</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Resume (PDF) *</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-alden-green transition-all cursor-pointer">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                      <p className="text-sm text-gray-500">PDF (Max 5MB)</p>
                      <input
                        type="file"
                        accept=".pdf"
                        required
                        className="hidden"
                        onChange={(e) => setFormData({...formData, resume: e.target.files[0]})}
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-alden-green hover:bg-alden-green-light text-white py-4 text-lg font-semibold">
                    Submit Application
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    Or email your resume to: <a href="mailto:careers@aldentechnologies.in" className="text-alden-green font-semibold hover:underline">careers@aldentechnologies.in</a>
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
